package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * 4DTE Weekly Trade — Remote-Typisierung analog Opening Put 2.1 (Spec §3-Muster).
 *
 * Underlying ist eine fixe Strategie-Konstante (SPY, ~4DTE, Delta 20–25 Put), ebenso
 * Zeitfenster und SL — die Remote-Params spiegeln exakt das, was auch die Desktop-View
 * anbietet: Konto, Anzahl, Transmit, Vorbedingungen ignorieren und optional auf das
 * Handelsfenster warten.
 */

val FOUR_DTE_STRATEGY_ID = StrategyId("fourDte")
const val FOUR_DTE_SCHEMA_VERSION = 1

@Serializable
@SerialName("fourDte")
data class FourDteParams(
    /** Konto (dynamische Auswahl, [FourDteOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Anzahl 1..10000 (Grenze der Strategie) */
    val quantity: Int,
    /**
     * PreCheck (Wochentag/Zeitfenster, Gap) übergehen — desktopseitig zusätzlich
     * an die Lizenz FEATURE_IGNORE_PRECONDITION gebunden (wie in der Desktop-View).
     * Teaminterne Funktion, nicht für Kunden.
     */
    val ignorePreconditions: Boolean = false,
    /**
     * erst bis 09:32 NY warten (Pendant zum „zeitgesteuert starten"-Button der View) —
     * desktopseitig an FEATURE_TIME_BASED_TRADING gebunden. Teaminterne Funktion,
     * nicht für Kunden; platziert ohne erneute Kontrakt-Bestätigung.
     */
    val waitForWindow: Boolean = false,
    /**
     * Vom Nutzer bestätigter Optionskontrakt in Symbol-Notation, aus `strategy.preview`
     * ([FourDtePreview.contract]). PFLICHT für den Sofort-Start (waitForWindow = false):
     * Der Desktop ermittelt den Kontrakt beim Platzieren erneut aus Live-Preisen und
     * platziert nur bei Übereinstimmung (Compliance). Bei waitForWindow = true ignoriert.
     * Nicht in Defaults/Presets persistieren — die Bestätigung gilt nur für den einen Start.
     */
    val expectedContract: String? = null,
    override val schemaVersion: Int = FOUR_DTE_SCHEMA_VERSION,
) : StrategyParams

@Serializable
@SerialName("fourDte")
data class FourDteOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /** Lizenz-Freigaben der teaminternen Schalter ([ChecklistGates]); Default gesperrt. */
    override val ignorePreconditionsAllowed: Boolean = false,
    override val waitForWindowAllowed: Boolean = false,
) : StrategyOptions, ChecklistGates

/**
 * Ergebnis von `strategy.preview` (Compliance-Vorschau für den Sofort-Start): der aus
 * Live-Preisen ermittelte Kontrakt plus alle Orders, die platziert würden. Muster
 * identisch zu [OpeningPutPreview].
 */
@Serializable
data class FourDtePreview(
    /** Symbol-Notation des ermittelten Kontrakts; null = Ermittlung fehlgeschlagen (siehe [message]). */
    val contract: String? = null,
    /** Anzeigefertige Zeile pro Order (Entry, Stop Loss, Time-Exit). */
    val orders: List<String> = emptyList(),
    /** Fehlermeldung, wenn kein Kontrakt ermittelt werden konnte. */
    val message: String? = null,
    /** Zweisprachige Fassung von [message]; null bei alten Desktops. */
    val messageText: RemoteText? = null,
    val asOf: Instant,
)

@Serializable
@SerialName("fourDte")
data class FourDteStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val steps: List<RemoteChecklistStep> = emptyList(),
    /** PreCheck wurde für diesen Lauf übersprungen */
    val preconditionsIgnored: Boolean = false,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = FOUR_DTE_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class FourDteBounds(
    val maxQuantity: Int? = 10,
    val allowTransmitToExchange: Boolean = false,
) : ParamsBounds<FourDteParams> {

    override fun violations(params: FourDteParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: FourDteParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxQuantity != null && params.quantity > maxQuantity) {
            violations += RemoteText(
                de = "quantity ${params.quantity} überschreitet das Limit $maxQuantity",
                en = "quantity ${params.quantity} exceeds the limit $maxQuantity",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

fun validateFourDte(params: FourDteParams, options: FourDteOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != FOUR_DTE_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $FOUR_DTE_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $FOUR_DTE_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $FOUR_DTE_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.quantity !in 1..10000) {
        errors += ValidationError(
            "quantity",
            "Anzahl muss zwischen 1 und 10000 liegen",
            messageText = RemoteText(
                de = "Anzahl muss zwischen 1 und 10000 liegen",
                en = "Quantity must be between 1 and 10000",
            ),
        )
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    errors += checklistGateErrors(params.ignorePreconditions, params.waitForWindow, options)
    return errors
}
